import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { dismissNuxTooltip, getNuxTooltipDismissals } from '../api/nuxTooltips'
import { trackNuxTooltipDismissed, trackNuxTooltipShown } from '../analytics/events'
import { useAuth } from '../contexts/AuthContext'
import { createNadDebug } from '../utils/debugLog'
import {
  NUX_TOOLTIP_COPY_KEYS,
  nuxTooltipCatalog,
  type NuxCompletion,
  type NuxCondition,
  type NuxSurface,
  type NuxTooltipCopy,
  type NuxTooltipDefinition,
} from './nuxCatalog'
import { addCachedNuxDismissal, readCachedNuxDismissals, writeCachedNuxDismissals } from './nuxDismissalCache'
import { NuxTooltip, type NuxTooltipPosition } from './NuxTooltip'
import { NuxTooltipEventsContext, type NuxEvent } from './nuxEvents'

const FEATURE_TAG = 'nux-tooltips'
const ANCHOR_GAP = 14
const VIEWPORT_GUTTER = 16
const TOOLTIP_WIDTH = 360
const TOOLTIP_HEIGHT_ESTIMATE = 190
const HIGHLIGHT_PADDING = 6
const nuxLog = createNadDebug('dw:nux-tooltips')

export const NUX_DISMISSALS_QUERY_KEY = ['nux-tooltips', 'dismissals']

interface NodeState {
  nodeId: string
  nodeType?: string
}

interface PanelState {
  panelId?: string
  nodeId?: string
  nodeType?: string
}

interface NuxRuntimeState {
  selectedNode: NodeState | null
  panelOpen: PanelState | null
  readyNodes: Map<string, NodeState>
}

interface TargetHighlight {
  top: number
  left: number
  width: number
  height: number
}

interface ActiveTooltip {
  key: string
  definition: NuxTooltipDefinition
  anchors: HTMLElement[]
  nodeId?: string
  nodeType?: string
  position: NuxTooltipPosition
  highlight: TargetHighlight
}

function currentSurface(pathname: string): NuxSurface | null {
  if (pathname === '/' || pathname === '/landing-v2') return 'landing'
  if (pathname.includes('/workspace') || pathname.includes('/director-desk')) return 'workspace'
  return null
}

function isElementVisible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect()
  const style = window.getComputedStyle(element)
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth &&
    style.visibility !== 'hidden' &&
    style.display !== 'none'
  )
}

function anchorsFor(definition: NuxTooltipDefinition): HTMLElement[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>(`[data-nux-anchor="${definition.anchorId}"]`),
  ).filter(isElementVisible)
}

function nodeTypeMatches(expected: string | undefined, actual: string | undefined): boolean {
  return expected === undefined || expected === actual
}

function valueMatches(expected: string | undefined, actual: string | undefined): boolean {
  return expected === undefined || expected === actual
}

function anchorNodeId(anchor: HTMLElement): string | undefined {
  return anchor.dataset.nuxNodeId
}

function anchorNodeType(anchor: HTMLElement): string | undefined {
  return anchor.dataset.nuxNodeType
}

function anchorNodeStatus(anchor: HTMLElement): string | undefined {
  return anchor.dataset.nuxNodeStatus
}

function isAnchorReady(anchor: HTMLElement, state: NuxRuntimeState): boolean {
  if (anchorNodeStatus(anchor) === 'completed') return true
  const nodeId = anchorNodeId(anchor)
  return Boolean(nodeId && state.readyNodes.has(nodeId))
}

function anchorMatchesSelected(anchor: HTMLElement, state: NuxRuntimeState): boolean {
  if (!state.selectedNode) return false
  if (!nodeTypeMatches(state.selectedNode.nodeType, anchorNodeType(anchor))) return false
  const nodeId = anchorNodeId(anchor)
  return !nodeId || nodeId === state.selectedNode.nodeId
}

function conditionPasses(
  condition: NuxCondition,
  anchors: HTMLElement[],
  state: NuxRuntimeState,
): boolean {
  if (condition.type === 'anchorVisible') return anchors.length > 0
  if (condition.type === 'nodeVisible') {
    return anchors.some((anchor) => nodeTypeMatches(condition.nodeType, anchorNodeType(anchor)))
  }
  if (condition.type === 'nodeReady') {
    return anchors.some((anchor) =>
      nodeTypeMatches(condition.nodeType, anchorNodeType(anchor)) && isAnchorReady(anchor, state),
    )
  }
  if (condition.type === 'nodeSelected') {
    return Boolean(
      state.selectedNode &&
        nodeTypeMatches(condition.nodeType, state.selectedNode.nodeType) &&
        anchors.some((anchor) => anchorMatchesSelected(anchor, state) || !anchorNodeId(anchor)),
    )
  }
  if (condition.type === 'panelOpen') {
    if (
      state.panelOpen &&
      valueMatches(condition.panelId, state.panelOpen.panelId) &&
      nodeTypeMatches(condition.nodeType, state.panelOpen.nodeType)
    ) {
      return true
    }
    return anchors.length > 0
  }
  return false
}

function candidateAnchorsFor(
  definition: NuxTooltipDefinition,
  anchors: HTMLElement[],
  state: NuxRuntimeState,
): HTMLElement[] {
  let candidates = anchors
  const readyCondition = definition.showWhen.find((condition) => condition.type === 'nodeReady')
  if (readyCondition?.type === 'nodeReady') {
    candidates = candidates.filter((anchor) =>
      nodeTypeMatches(readyCondition.nodeType, anchorNodeType(anchor)) && isAnchorReady(anchor, state),
    )
  }
  const visibleCondition = definition.showWhen.find((condition) => condition.type === 'nodeVisible')
  if (visibleCondition?.type === 'nodeVisible') {
    candidates = candidates.filter((anchor) => nodeTypeMatches(visibleCondition.nodeType, anchorNodeType(anchor)))
  }
  const selectedCondition = definition.showWhen.find((condition) => condition.type === 'nodeSelected')
  if (selectedCondition?.type === 'nodeSelected' && state.selectedNode) {
    candidates = candidates.filter((anchor) => {
      const nodeId = anchorNodeId(anchor)
      const nodeType = anchorNodeType(anchor)
      return (
        nodeTypeMatches(selectedCondition.nodeType, state.selectedNode?.nodeType) &&
        (!nodeType || nodeTypeMatches(nodeType, state.selectedNode?.nodeType)) &&
        (!nodeId || nodeId === state.selectedNode?.nodeId)
      )
    })
  }
  return candidates
}

function unionRect(anchors: HTMLElement[]): DOMRect {
  const rects = anchors.map((anchor) => anchor.getBoundingClientRect())
  const left = Math.min(...rects.map((rect) => rect.left))
  const top = Math.min(...rects.map((rect) => rect.top))
  const right = Math.max(...rects.map((rect) => rect.right))
  const bottom = Math.max(...rects.map((rect) => rect.bottom))
  return new DOMRect(left, top, right - left, bottom - top)
}

function computePosition(rect: DOMRect, placement: NuxTooltipPosition['placement']): NuxTooltipPosition {
  let nextPlacement = placement

  const fitsRight = rect.right + ANCHOR_GAP + TOOLTIP_WIDTH <= window.innerWidth - VIEWPORT_GUTTER
  const fitsLeft = rect.left - ANCHOR_GAP - TOOLTIP_WIDTH >= VIEWPORT_GUTTER
  const fitsTop = rect.top - ANCHOR_GAP - TOOLTIP_HEIGHT_ESTIMATE >= VIEWPORT_GUTTER
  const fitsBottom = rect.bottom + ANCHOR_GAP + TOOLTIP_HEIGHT_ESTIMATE <= window.innerHeight - VIEWPORT_GUTTER

  if (nextPlacement === 'right' && !fitsRight && fitsLeft) nextPlacement = 'left'
  if (nextPlacement === 'left' && !fitsLeft && fitsRight) nextPlacement = 'right'
  if (nextPlacement === 'top' && !fitsTop && fitsBottom) nextPlacement = 'bottom'
  if (nextPlacement === 'bottom' && !fitsBottom && fitsTop) nextPlacement = 'top'

  let top = rect.top
  let left = rect.left
  if (nextPlacement === 'right') {
    top = rect.top + rect.height / 2 - TOOLTIP_HEIGHT_ESTIMATE / 2
    left = rect.right + ANCHOR_GAP
  } else if (nextPlacement === 'left') {
    top = rect.top + rect.height / 2 - TOOLTIP_HEIGHT_ESTIMATE / 2
    left = rect.left - TOOLTIP_WIDTH - ANCHOR_GAP
  } else if (nextPlacement === 'top') {
    top = rect.top - TOOLTIP_HEIGHT_ESTIMATE - ANCHOR_GAP
    left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2
  } else {
    top = rect.bottom + ANCHOR_GAP
    left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2
  }

  top = Math.max(VIEWPORT_GUTTER, Math.min(top, window.innerHeight - VIEWPORT_GUTTER - TOOLTIP_HEIGHT_ESTIMATE))
  left = Math.max(VIEWPORT_GUTTER, Math.min(left, window.innerWidth - VIEWPORT_GUTTER - Math.min(TOOLTIP_WIDTH, window.innerWidth - 32)))

  return { top, left, placement: nextPlacement }
}

function computeHighlight(rect: DOMRect): TargetHighlight {
  return {
    top: Math.max(0, rect.top - HIGHLIGHT_PADDING),
    left: Math.max(0, rect.left - HIGHLIGHT_PADDING),
    width: rect.width + HIGHLIGHT_PADDING * 2,
    height: rect.height + HIGHLIGHT_PADDING * 2,
  }
}

function buildActiveTooltip(
  definition: NuxTooltipDefinition,
  anchors: HTMLElement[],
  state: NuxRuntimeState,
): ActiveTooltip | null {
  const candidates = candidateAnchorsFor(definition, anchors, state)
  if (candidates.length === 0) return null
  const activeAnchors = definition.anchorStrategy === 'union' ? candidates : [candidates[0]]
  const rect = unionRect(activeAnchors)
  const nodeId = activeAnchors.map(anchorNodeId).find(Boolean)
  const nodeType = activeAnchors.map(anchorNodeType).find(Boolean)
  const key = nodeId ? `${definition.id}:${nodeId}` : definition.id
  return {
    key,
    definition,
    anchors: activeAnchors,
    nodeId,
    nodeType,
    position: computePosition(rect, definition.placement),
    highlight: computeHighlight(rect),
  }
}

function completionMatches(completion: NuxCompletion, event: NuxEvent, active: ActiveTooltip): boolean {
  if (completion.type !== event.type) return false
  const scopeToActiveNode = completion.type !== 'nodeSelected' || completion.scope !== 'anyNode'
  if (scopeToActiveNode && active.nodeId && 'nodeId' in event && event.nodeId && event.nodeId !== active.nodeId) return false

  if (completion.type === 'nodeSelected' && event.type === 'nodeSelected') {
    const expectedNodeType = completion.nodeType ?? (completion.scope === 'anyNode' ? undefined : active.nodeType)
    return nodeTypeMatches(expectedNodeType, event.nodeType)
  }
  if (completion.type === 'panelOpened' && event.type === 'panelOpened') {
    return valueMatches(completion.panelId, event.panelId) && nodeTypeMatches(completion.nodeType ?? active.nodeType, event.nodeType)
  }
  if (completion.type === 'editorOpened' && event.type === 'editorOpened') {
    return valueMatches(completion.editorId, event.editorId)
  }
  if (completion.type === 'exportCompleted' && event.type === 'exportCompleted') {
    return valueMatches(completion.editorId, event.editorId)
  }
  if (completion.type === 'voiceAdded' && event.type === 'voiceAdded') {
    return nodeTypeMatches(completion.nodeType ?? active.nodeType, event.nodeType)
  }
  if (completion.type === 'controlActivated' && event.type === 'controlActivated') {
    return (
      valueMatches(completion.controlId, event.controlId) &&
      valueMatches(completion.anchorId, event.anchorId) &&
      nodeTypeMatches(completion.nodeType ?? active.nodeType, event.nodeType)
    )
  }
  return false
}

function isNuxEventDetail(value: object | null | undefined): value is NuxEvent {
  if (!value || !('type' in value) || typeof value.type !== 'string') return false
  if (value.type === 'nodeSelected' || value.type === 'nodeReady') {
    return 'nodeId' in value && typeof value.nodeId === 'string'
  }
  if (value.type === 'controlActivated') {
    return 'controlId' in value && typeof value.controlId === 'string'
  }
  return (
    value.type === 'panelOpened' ||
    value.type === 'editorOpened' ||
    value.type === 'exportCompleted' ||
    value.type === 'voiceAdded'
  )
}

export function NuxTooltipProvider({ children, enabled = true }: { children: React.ReactNode; enabled?: boolean }) {
  const { user, isAuthenticated, isLoading } = useAuth()
  const { t } = useTranslation('canvas')
  const location = useLocation()
  const queryClient = useQueryClient()
  const userId = user?.id ?? null
  const surface = currentSurface(location.pathname)
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const [cacheReady, setCacheReady] = useState(false)
  const [activeTips, setActiveTips] = useState<ActiveTooltip[]>([])
  const [domRevision, setDomRevision] = useState(0)
  const runtimeStateRef = useRef<NuxRuntimeState>({
    selectedNode: null,
    panelOpen: null,
    readyNodes: new Map(),
  })
  const activeTipsRef = useRef<ActiveTooltip[]>([])
  const shownKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    activeTipsRef.current = activeTips
  }, [activeTips])

  useEffect(() => {
    if (!userId) {
      setDismissedIds(new Set())
      setCacheReady(false)
      setActiveTips([])
      return
    }
    setDismissedIds(readCachedNuxDismissals(userId))
    setCacheReady(true)
  }, [userId])

  const dismissalsQuery = useQuery({
    queryKey: [...NUX_DISMISSALS_QUERY_KEY, userId],
    queryFn: getNuxTooltipDismissals,
    enabled: Boolean(enabled && isAuthenticated && userId && cacheReady),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  useEffect(() => {
    if (!userId || !dismissalsQuery.data) return
    const next = new Set([...readCachedNuxDismissals(userId), ...dismissalsQuery.data])
    setDismissedIds(next)
    writeCachedNuxDismissals(userId, next)
  }, [dismissalsQuery.data, userId])

  const { mutate: mutateDismiss } = useMutation({
    mutationFn: (tooltipId: string) => dismissNuxTooltip(tooltipId),
    onError: (error, tooltipId) => {
      nuxLog({
        event: 'dismiss-failed',
        feature: FEATURE_TAG,
        tooltip_id: tooltipId,
        error: error instanceof Error ? error.message : String(error),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NUX_DISMISSALS_QUERY_KEY })
    },
  })

  const readyToShow = Boolean(
    enabled &&
      !isLoading &&
      isAuthenticated &&
      userId &&
      cacheReady &&
      surface &&
      (dismissalsQuery.isSuccess || dismissalsQuery.isError),
  )

  const dismissTooltip = useCallback((activeKey: string, source: 'skipped' | 'completed') => {
    const current = activeTipsRef.current.find((tip) => tip.key === activeKey)
    if (!current || !userId) return
    const tooltipId = current.definition.id
    setActiveTips((tips) => tips.filter((tip) => tip.definition.id !== tooltipId))
    setDismissedIds(addCachedNuxDismissal(userId, tooltipId))
    mutateDismiss(tooltipId)
    trackNuxTooltipDismissed({
      tooltip_id: tooltipId,
      app_area: current.definition.surface,
      dismiss_source: source,
      ...(current.nodeType ? { node_type: current.nodeType } : {}),
    })
    nuxLog({ event: 'dismiss', feature: FEATURE_TAG, tooltip_id: tooltipId, dismiss_source: source })
  }, [mutateDismiss, userId])

  const emitNuxEvent = useCallback((event: NuxEvent) => {
    if (event.type === 'nodeSelected') {
      runtimeStateRef.current = {
        ...runtimeStateRef.current,
        selectedNode: { nodeId: event.nodeId, nodeType: event.nodeType },
      }
    } else if (event.type === 'nodeReady') {
      const readyNodes = new Map(runtimeStateRef.current.readyNodes)
      readyNodes.set(event.nodeId, { nodeId: event.nodeId, nodeType: event.nodeType })
      runtimeStateRef.current = { ...runtimeStateRef.current, readyNodes }
    } else if (event.type === 'panelOpened') {
      runtimeStateRef.current = {
        ...runtimeStateRef.current,
        panelOpen: {
          panelId: event.panelId,
          nodeId: event.nodeId,
          nodeType: event.nodeType,
        },
      }
    } else if (event.type === 'controlActivated' && event.controlId === 'canvasInteracted') {
      runtimeStateRef.current = {
        ...runtimeStateRef.current,
        selectedNode: runtimeStateRef.current.selectedNode,
      }
    }

    const completedKeys = enabled
      ? activeTipsRef.current
          .filter((tip) => tip.definition.completeWhen?.some((completion) => completionMatches(completion, event, tip)))
          .map((tip) => tip.key)
      : []
    for (const key of completedKeys) dismissTooltip(key, 'completed')
    setDomRevision((revision) => revision + 1)
  }, [dismissTooltip, enabled])

  useEffect(() => {
    if (!import.meta.env.DEV) return
    const handleDevEvent = (event: Event) => {
      if (!(event instanceof CustomEvent)) return
      const detail = event.detail
      if (!isNuxEventDetail(detail)) return
      emitNuxEvent(detail)
    }
    window.addEventListener('dreamwrite:nux-event', handleDevEvent)
    return () => window.removeEventListener('dreamwrite:nux-event', handleDevEvent)
  }, [emitNuxEvent])

  useEffect(() => {
    if (!enabled) return
    const bump = () => setDomRevision((revision) => revision + 1)
    const observer = new MutationObserver(bump)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'data-nux-anchor',
        'data-nux-node-id',
        'data-nux-node-type',
        'data-nux-node-status',
        'data-nux-control-id',
        'data-nux-panel-id',
        'style',
        'class',
      ],
    })
    window.addEventListener('resize', bump)
    window.addEventListener('scroll', bump, true)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', bump)
      window.removeEventListener('scroll', bump, true)
    }
  }, [enabled])

  useEffect(() => {
    if (!readyToShow || !surface) {
      setActiveTips([])
      return
    }

    const nextTips: ActiveTooltip[] = []
    for (const definition of nuxTooltipCatalog) {
      if (definition.surface !== surface || dismissedIds.has(definition.id)) continue
      const anchors = anchorsFor(definition)
      if (anchors.length === 0) continue
      if (!definition.showWhen.every((condition) => conditionPasses(condition, anchors, runtimeStateRef.current))) {
        continue
      }
      const activeTip = buildActiveTooltip(definition, anchors, runtimeStateRef.current)
      if (!activeTip) continue
      nextTips.push(activeTip)
      if (!shownKeysRef.current.has(activeTip.key)) {
        shownKeysRef.current.add(activeTip.key)
        trackNuxTooltipShown({
          tooltip_id: definition.id,
          app_area: definition.surface,
          ...(activeTip.nodeType ? { node_type: activeTip.nodeType } : {}),
        })
        nuxLog({ event: 'show', feature: FEATURE_TAG, tooltip_id: definition.id, active_key: activeTip.key })
      }
    }

    setActiveTips(nextTips)
  }, [dismissedIds, domRevision, readyToShow, surface, location.pathname])

  const contextValue = useMemo(() => ({ emitNuxEvent }), [emitNuxEvent])
  const portal = enabled && activeTips.length > 0
    ? createPortal(
        <>
          {activeTips.map((tip) => {
            const activeCopy: NuxTooltipCopy = {
              title: t(NUX_TOOLTIP_COPY_KEYS[tip.definition.copyKey].titleKey),
              body: t(NUX_TOOLTIP_COPY_KEYS[tip.definition.copyKey].bodyKey),
              skip: t(NUX_TOOLTIP_COPY_KEYS[tip.definition.copyKey].skipKey),
            }
            return (
              <div key={tip.key}>
                <div
                  className="pointer-events-none fixed z-[85] rounded-lg border-2 border-primary bg-primary/10 ring-4 ring-primary/10"
                  style={{
                    top: tip.highlight.top,
                    left: tip.highlight.left,
                    width: tip.highlight.width,
                    height: tip.highlight.height,
                  }}
                  data-testid="nux-tooltip-highlight"
                  data-nux-tooltip-id={tip.definition.id}
                />
                <NuxTooltip
                  id={tip.definition.id}
                  copy={activeCopy}
                  position={tip.position}
                  onSkip={() => dismissTooltip(tip.key, 'skipped')}
                />
              </div>
            )
          })}
        </>,
        document.body,
      )
    : null

  return (
    <NuxTooltipEventsContext.Provider value={contextValue}>
      {children}
      {portal}
    </NuxTooltipEventsContext.Provider>
  )
}
